"use client";

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import axios from "axios";
import { toast } from "react-toastify";
import Script from "next/script";
import { renewToken as renewTokenApi } from "@/services/api/renew-token/token";
const LOCATION_STORAGE_KEY = "user_location";
const LOCATION_PERMISSION_KEY = "location_permission_granted";
const IP_LOCATION_API = "https://ipapi.co/json/";
const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_ROUTES = ["/checkout", "/partner-with-us"];

// Added: Constants for token keys
const TOKEN_KEYS = {
  ACCESS_TOKEN: "accessToken",
  ID_TOKEN: "idToken",
  REFRESH_TOKEN: "refreshToken",
  EXPIRES: "expires",
  USER: "user_data"
};
const ACCESS_TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour in milliseconds

interface UserContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (authState: boolean) => void;
  token: string | null;
  setToken: (token: string | null) => void;
  renewToken: () => Promise<boolean>; // Modified to return success status
  location: {
    lat: number;
    lng: number;
  } | null;
  logout: () => void;
  setLocation: (location: {
    lat: number;
    lng: number;
  } | null) => void;
  showLocationModal: boolean;
  setShowLocationModal: (show: boolean) => void;
  requestLocationPermission: () => Promise<void>;
  watchLocation: () => void;
  stopWatchingLocation: () => void;
  isPageLoading: boolean;
  setHomepageSelectedFilters: React.Dispatch<React.SetStateAction<HomePageFilters[]>>;
  homepageSelectedFilters: HomePageFilters[];
  isGoogleMapsLoaded: boolean;
  isAuthLoading: boolean; // Added: flag to track auth loading state
  user: Record<string, unknown> | null;
  setUser: (user: Record<string, unknown> | null) => void;
}
interface HomePageFilters {
  title: string;
  subtitle: string;
  image: string;
  type: string;
  id: string;
  isGoogleMapsLoaded?: boolean;
}
interface IpLocationResponse {
  latitude: number;
  longitude: number;
  error?: boolean;
}
const UserContext = createContext<UserContextType | undefined>(undefined);

// Safe browser API access helpers
const isBrowser = () => typeof window !== "undefined";
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (isBrowser()) {
      return localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    if (isBrowser()) {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key: string): void => {
    if (isBrowser()) {
      localStorage.removeItem(key);
    }
  }
};

// Helper to set cookies from client
const setCookie = (name: string, value: string, days: number) => {
  if (!isBrowser()) return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

// Helper to get cookies from client
const getCookie = (name: string): string | null => {
  if (!isBrowser()) return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
};

// Helper to delete cookies from client
const deleteCookie = (name: string) => {
  if (!isBrowser()) return;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict`;
};
const safeSessionStorage = {
  getItem: (key: string): string | null => {
    if (isBrowser()) {
      return sessionStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    if (isBrowser()) {
      sessionStorage.setItem(key, value);
    }
  },
  removeItem: (key: string): void => {
    if (isBrowser()) {
      sessionStorage.removeItem(key);
    }
  }
};
export const UserProvider = ({
  children,
  initialToken
}: {
  children: React.ReactNode;
  initialToken?: string | null;
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialToken);
  const [token, setToken] = useState<string | null>(initialToken || null);
  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [locationError, setLocationError] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [homepageSelectedFilters, setHomepageSelectedFilters] = useState<HomePageFilters[]>([]);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(!initialToken); // Added: new state to track auth loading
  const [user, setUser] = useState<Record<string, unknown> | null>(null);

  // Added: Ref to track token refresh in progress

  const tokenRefreshInProgress = useRef<boolean>(false);
  const pathname = usePathname();
  const router = useRouter();

  // Handle Google Maps API loading
  const handleGoogleMapsLoaded = () => {
    setIsGoogleMapsLoaded(true);
  };
  const handleGoogleMapsError = () => {
    console.error("Failed to load Google Maps API");
    setIsGoogleMapsLoaded(false);
  };

  // Improved token renewal function
  const renewToken = useCallback(async (): Promise<boolean> => {
    // Prevent multiple simultaneous renewal attempts
    if (tokenRefreshInProgress.current) {
      // Wait for the current renewal to finish
      await new Promise(resolve => setTimeout(resolve, 1000));
      return isAuthenticated;
    }
    tokenRefreshInProgress.current = true;
    setIsAuthLoading(true);
    try {
      const refreshToken = safeLocalStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        setIsAuthenticated(false);
        setIsAuthLoading(false);
        tokenRefreshInProgress.current = false;
        return false;
      }
      const response = await renewTokenApi({
        token: refreshToken
      });
      const {
        refresh,
        access
      } = response;

      // Set expiration time manually (e.g., 1 hour from now)
      const expirationTime = Date.now() + ACCESS_TOKEN_LIFETIME;
      safeLocalStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, access);
      safeLocalStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refresh);
      safeLocalStorage.setItem(TOKEN_KEYS.EXPIRES, expirationTime.toString());
      setCookie(TOKEN_KEYS.ACCESS_TOKEN, access, 30); // Save to cookies for SSR

      setToken(access);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error("Token renewal failed:", error);
      // Clear tokens on failure
      setToken(null);
      setIsAuthenticated(false);

      // Clear from storage
      safeLocalStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.EXPIRES);
      deleteCookie(TOKEN_KEYS.ACCESS_TOKEN);
      return false;
    } finally {
      setIsAuthLoading(false);
      tokenRefreshInProgress.current = false;
    }
  }, [isAuthenticated]);

  // Set isClient to true once the component is mounted
  useEffect(() => {
    setIsClient(true);

    // Initialize location and permission status from localStorage
    try {
      const savedLocation = safeLocalStorage.getItem(LOCATION_STORAGE_KEY);
      if (savedLocation) {
        setLocation(JSON.parse(savedLocation));
      }

      // Check if location permission was previously granted
      const permissionGranted = safeLocalStorage.getItem(LOCATION_PERMISSION_KEY);
      if (permissionGranted === "true") {
        setLocationPermissionGranted(true);
        setHasRequestedLocation(true);
      }

      // Restore user data
      const savedUser = safeLocalStorage.getItem(TOKEN_KEYS.USER);
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    } catch (e) {
      console.error("Error parsing saved data:", e);
    }
  }, []);

  // Enhanced isTokenExpired checker
  const isTokenExpired = useCallback((): boolean => {
    if (!isClient) return true;
    try {
      const accessToken = safeLocalStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
      const expires = safeLocalStorage.getItem(TOKEN_KEYS.EXPIRES);
      if (!accessToken || !expires) {
        return true;
      }
      if (expires) {
        const expirationTime_0 = parseInt(expires);
        const currentTime = Date.now();
        // Add 5-minute buffer to avoid edge cases
        return currentTime >= expirationTime_0 - 5 * 60 * 1000;
      }

      // Default to expired if we can't determine
      return true;
    } catch (e_0) {
      console.error("Error checking token expiration:", e_0);
      return true;
    }
  }, [isClient]);

  // Custom setLocation wrapper
  const updateLocation = useCallback((newLocation: {
    lat: number;
    lng: number;
  } | null) => {
    if (!isClient) return;
    setLocation(newLocation);
    if (newLocation) {
      try {
        safeLocalStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
        safeLocalStorage.setItem(LOCATION_PERMISSION_KEY, "true");
        setLocationPermissionGranted(true);
        setHasRequestedLocation(true);
      } catch (e_1) {
        console.error("Error saving to localStorage:", e_1);
      }
    }
  }, [isClient]);

  // Improved checkSession function
  const checkSession = useCallback(async () => {
    if (!isClient) return;
    setIsAuthLoading(true);
    try {
      // First check localStorage
      const accessToken_0 = safeLocalStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);

      // If no token in localStorage, check cookies (this helps after page refreshes)
      const cookieToken = getCookie(TOKEN_KEYS.ACCESS_TOKEN);
      if (!accessToken_0 && !cookieToken) {
        setIsAuthenticated(false);
        setToken(null);
        setIsAuthLoading(false);
        return;
      }

      // If token exists but wasn't in state, update the state
      if ((accessToken_0 || cookieToken) && !token) {
        const tokenToUse = accessToken_0 || cookieToken;
        setToken(tokenToUse);
      }

      // Check if token is expired
      if (isTokenExpired()) {
        await renewToken();
      } else {
        setIsAuthenticated(true);
      }
    } catch (e_2) {
      console.error("Error checking session:", e_2);
      setIsAuthenticated(false);
      setToken(null);
    } finally {
      setIsAuthLoading(false);
    }
  }, [isClient, isTokenExpired, renewToken, token]);

  // Get location from IP address when browser geolocation fails
  const getLocationFromIP = useCallback(async (): Promise<{
    lat: number;
    lng: number;
  } | null> => {
    if (!isClient) return null;
    try {
      const response_0 = await axios.get<IpLocationResponse>(IP_LOCATION_API);
      if (!response_0.data || response_0.data.error) {
        console.error("Error getting location from IP");
        return null;
      }
      const {
        latitude,
        longitude
      } = response_0.data;

      // Validate that we received actual coordinates
      if (typeof latitude !== "number" || typeof longitude !== "number") {
        console.error("Invalid coordinates from IP API:", response_0.data);
        return null;
      }
      return {
        lat: latitude,
        lng: longitude
      };
    } catch (error_0) {
      console.error("Error fetching location from IP:", error_0);
      return null;
    }
  }, [isClient]);
  const requestLocationPermission = useCallback(async () => {
    if (!isClient) return;
    setHasRequestedLocation(true);
    if (!isBrowser() || !navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      setLocationError(true);
      const ipLocation = await getLocationFromIP();
      if (ipLocation) {
        updateLocation(ipLocation);
        setShowLocationModal(false);
      }
      return;
    }
    try {
      const position = await new Promise<GeolocationPosition>((resolve_0, reject) => {
        navigator.geolocation.getCurrentPosition(pos => resolve_0(pos), err => reject(err), {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      // Browser geolocation successful
      const {
        latitude: latitude_0,
        longitude: longitude_0
      } = position.coords;
      updateLocation({
        lat: latitude_0,
        lng: longitude_0
      });
      setLocationError(false);
      setShowLocationModal(false);
    } catch (error_1) {
      // Browser geolocation failed, log the error
      console.error("Browser geolocation failed:", error_1);
      setLocationError(true);
      try {
        // Fall back to IP-based location
        const ipLocation_0 = await getLocationFromIP();
        if (ipLocation_0) {
          updateLocation(ipLocation_0);
          setShowLocationModal(false);
        } else {
          console.error("IP-based location failed, no location available");
        }
      } catch (ipError) {
        console.error("Error getting IP-based location:", ipError);
      }
    }
  }, [isClient, getLocationFromIP, updateLocation]);

  // Simplified logout function
  const logout = useCallback(async () => {
    if (!isClient) return;
    try {
      setIsAuthLoading(true);

      // Clear localStorage tokens set during register API call success
      safeLocalStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
      safeLocalStorage.removeItem(TOKEN_KEYS.EXPIRES);
      safeLocalStorage.removeItem(TOKEN_KEYS.USER);
      deleteCookie(TOKEN_KEYS.ACCESS_TOKEN);
      // Update state
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);

      // Show success toast message
      toast.success("Logout successful!");

      // Redirect to home page after logout
      router.push("/");
    } catch (error_2) {
      console.error("Error during logout:", error_2);
      toast.error("Error logging out. Please try again.");
    } finally {
      setIsAuthLoading(false);
    }
  }, [isClient, router]);
  const watchLocation = useCallback(() => {
    if (!isClient || !isBrowser() || !navigator.geolocation) {
      console.error("Geolocation is not supported or not on client");
      return;
    }
    const id = navigator.geolocation.watchPosition(position_0 => {
      const {
        latitude: latitude_1,
        longitude: longitude_1
      } = position_0.coords;
      updateLocation({
        lat: latitude_1,
        lng: longitude_1
      });
    }, error_3 => {
      console.error("Error watching location:", error_3);
      setLocationError(true);
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
    setWatchId(id);
  }, [isClient, updateLocation]);
  const stopWatchingLocation = useCallback(() => {
    if (!isClient || !isBrowser()) return;
    if (watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
  }, [watchId, isClient]);

  // Check session when client is ready
  useEffect(() => {
    if (isClient) {
      checkSession();
    }
  }, [isClient, checkSession]);

  // Track page loading state - only run on client
  useEffect(() => {
    if (!isClient) return;

    // Set a marker in session storage so we know we're transitioning
    try {
      const isTransitioning = safeSessionStorage.getItem("pageTransitioning") === "true";
      safeSessionStorage.setItem("pageTransitioning", "true");

      // This means we're coming from a navigation (not an initial load)
      if (isTransitioning) {
        setIsPageLoading(true);
        safeSessionStorage.removeItem("pageTransitioning");
      }
    } catch (e_3) {
      console.error("Error accessing sessionStorage:", e_3);
    }

    // Small delay to account for component render time
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [pathname, isClient]);

  // Only show the location modal when needed and after page loading completes
  useEffect(() => {
    if (!isClient) return;
    if (pathname === "/") return;
    if (isPageLoading) return;

    // Don't show modal if we already have location or already requested/granted permission
    if (location || locationPermissionGranted || hasRequestedLocation || locationError) return;
    setShowLocationModal(true);
  }, [location, pathname, hasRequestedLocation, isPageLoading, locationError, isClient, locationPermissionGranted]);

  // Added: Set up automatic token refresh
  useEffect(() => {
    if (!isClient || !isAuthenticated) return;
    const checkTokenExpiry = () => {
      const accessToken_1 = safeLocalStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
      const expires_0 = safeLocalStorage.getItem(TOKEN_KEYS.EXPIRES);
      if (!accessToken_1 || !expires_0) return;
      const expirationTime_1 = parseInt(expires_0);
      const currentTime_0 = Date.now();
      const timeToExpiry = expirationTime_1 - currentTime_0;

      // If token expires in less than 5 minutes, refresh it
      if (timeToExpiry < 5 * 60 * 1000 && timeToExpiry > 0) {
        renewToken();
      }
    };

    // Check token expiry every minute
    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [isClient, isAuthenticated, renewToken]);
  const contextValue = useMemo(() => ({
    isAuthenticated,
    setIsAuthenticated,
    token,
    setToken,
    renewToken,
    location,
    logout,
    setLocation: updateLocation,
    showLocationModal,
    setShowLocationModal,
    requestLocationPermission,
    watchLocation,
    stopWatchingLocation,
    isPageLoading,
    setHomepageSelectedFilters,
    homepageSelectedFilters,
    isGoogleMapsLoaded,
    isAuthLoading,
    // Added: expose loading state
    user,
    setUser
  }), [isAuthenticated, token, renewToken, location, logout, updateLocation, showLocationModal, requestLocationPermission, watchLocation, stopWatchingLocation, isPageLoading, homepageSelectedFilters, isGoogleMapsLoaded, isAuthLoading, user]);
  return <UserContext.Provider value={contextValue}>
      {/* Google Maps API Script - Only load on routes that require it */}
      {GOOGLE_MAPS_ROUTES.some(route => pathname?.includes(route)) && <Script strategy="lazyOnload" src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker&loading=async`} onLoad={handleGoogleMapsLoaded} onError={handleGoogleMapsError} />}
      {children}
    </UserContext.Provider>;
};
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};